import runsApi from "@apis/runs";
import { ERRORS } from "@constants/texts/errors";
import { MESSAGES } from "@constants/texts/messages";
import consoleLogFormatted from "consoleLogFormatted";
import pRetry, { AbortError } from "p-retry";
class HeartbeatManagerService {
    // eslint-disable-next-line @bigbinary/neeto/neetocommons-tips
    constructor(ciBuildId, options = {
        interval: 40000,
        maxRetries: 3,
        requestTimeout: 3000,
        retryDelay: 1000,
        emergencyThreshold: 100000,
    }) {
        this.ciBuildId = ciBuildId;
        this.options = options;
        this.interval = null;
        this.emergencyTimeout = null;
        this.isServiceActive = false;
        this.isHeartbeatInProgress = false;
        this.lastSuccessfulHeartbeat = 0;
        this.start = async () => {
            if (this.isServiceActive)
                return;
            this.isServiceActive = true;
            this.lastSuccessfulHeartbeat = Date.now();
            await this.sendHeartbeat();
            this.scheduleNextHeartbeat();
            void this.scheduleEmergencyCheck();
        };
        this.stop = () => {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
            if (this.emergencyTimeout) {
                clearTimeout(this.emergencyTimeout);
                this.emergencyTimeout = null;
            }
            this.isServiceActive = false;
        };
        this.scheduleNextHeartbeat = () => {
            this.interval = setInterval(() => {
                if (!this.isHeartbeatInProgress) {
                    this.sendHeartbeat().catch(error => {
                        var _a;
                        consoleLogFormatted.error(MESSAGES.heartbeat.schedulingFailed((_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : String(error)));
                    });
                }
            }, this.options.interval);
        };
        this.scheduleEmergencyCheck = () => {
            this.emergencyTimeout = setTimeout(() => {
                if (Date.now() - this.lastSuccessfulHeartbeat >
                    this.options.emergencyThreshold) {
                    consoleLogFormatted.warning(MESSAGES.heartbeat.emergencyTriggered);
                    if (!this.isHeartbeatInProgress) {
                        void this.sendEmergencyHeartbeat();
                    }
                }
                if (this.isServiceActive) {
                    void this.scheduleEmergencyCheck();
                }
            }, this.options.emergencyThreshold);
        };
        this.sendHeartbeat = async () => {
            if (this.isHeartbeatInProgress)
                return;
            this.isHeartbeatInProgress = true;
            try {
                await pRetry(async () => {
                    await runsApi.heartbeat(this.ciBuildId);
                    this.lastSuccessfulHeartbeat = Date.now();
                }, {
                    retries: this.options.maxRetries,
                    minTimeout: this.options.retryDelay,
                    maxTimeout: this.options.retryDelay,
                    onFailedAttempt: (error) => {
                        var _a;
                        const axiosError = error;
                        if ((_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.status) {
                            const status = axiosError.response.status;
                            if (status >= 400 &&
                                status < 500 &&
                                status !== 404 &&
                                status !== 408) {
                                throw new AbortError(`Non-retriable client error: ${status}`);
                            }
                        }
                        consoleLogFormatted.warning(MESSAGES.heartbeat.attemptFailed(error.attemptNumber, error.retriesLeft, error.message));
                    },
                });
            }
            catch (error) {
                this.handleHeartbeatFailure(error);
            }
            finally {
                this.isHeartbeatInProgress = false;
            }
        };
        this.sendEmergencyHeartbeat = async () => {
            var _a;
            this.isHeartbeatInProgress = true;
            try {
                await runsApi.heartbeat(this.ciBuildId);
                this.lastSuccessfulHeartbeat = Date.now();
            }
            catch (error) {
                consoleLogFormatted.error(MESSAGES.heartbeat.emergencyFailed((_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : String(error)));
                this.handleHeartbeatFailure(error);
            }
            finally {
                this.isHeartbeatInProgress = false;
            }
        };
        this.handleHeartbeatFailure = (error) => {
            var _a;
            const isAbortError = error instanceof AbortError;
            consoleLogFormatted.error(MESSAGES.heartbeat.failed((_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : String(error)));
            if (isAbortError) {
                consoleLogFormatted.redText(ERRORS.heartbeat.stopped);
                this.stop();
                process.exit(1);
            }
        };
        this.sendImmediateHeartbeat = async () => {
            if (!this.isHeartbeatInProgress) {
                await this.sendHeartbeat();
            }
        };
    }
    get status() {
        return {
            isServiceActive: this.isServiceActive,
            isHeartbeatInProgress: this.isHeartbeatInProgress,
            lastHeartbeat: this.lastSuccessfulHeartbeat,
        };
    }
}
export default HeartbeatManagerService;
//# sourceMappingURL=heartbeatManager.js.map